import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

export interface TestPaymentRequest {
  organizationId: number;
  amount: number;
  currency: string;
  reference?: string;
}

export interface TestPaymentResponse {
  paymentRequestId: string;
  status: string;
  providerTransactionId?: string;
  message?: string;
  processedAtUtc: string;
}

export interface HelcimAddressResponse {
  name?: string;
  street1?: string;
  street2?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
}

export interface HelcimCustomerResponse {
  organizationId: number;
  customerId: string;
  customerCode?: string;
  contactName?: string;
  businessName?: string;
  cellPhone?: string;
  billingAddress?: HelcimAddressResponse;
  shippingAddress?: HelcimAddressResponse;
}

export interface HelcimCustomerUpdateRequest {
  organizationId: number;
  idempotencyKey?: string;
  customerId: string;
  customerCode?: string;
  contactName?: string;
  businessName?: string;
  cellPhone?: string;
  billingAddress?: HelcimAddressRequest;
  shippingAddress?: HelcimAddressRequest;
}

export interface HelcimCustomerListRequest {
  organizationId?: number;
  search?: string;
  customerCode?: string;
  limit?: number;
  page?: number;
  includeCards?: boolean;
}

export interface HelcimCustomerListResponse {
  page?: number;
  limit?: number;
  totalCount?: number;
  customers: HelcimCustomerResponse[];
}

export interface HelcimAddressRequest {
  name: string;
  street1: string;
  street2?: string;
  city: string;
  province?: string;
  country?: string;
  postalCode: string;
  phone?: string;
  email?: string;
}

export interface HelcimCustomerCreateRequest {
  organizationId: number;
  idempotencyKey?: string;
  customerCode: string;
  contactName?: string;
  businessName?: string;
  cellPhone?: string;
  billingAddress: HelcimAddressRequest;
  shippingAddress: HelcimAddressRequest;
}

export interface HelcimInvoiceLineItemContract {
  description: string;
  quantity: number;
  price: number;
  taxAmount?: number;
  discountAmount?: number;
  sku?: string;
}

export interface HelcimInvoicePickupContract {
  name?: string;
  date?: string;
}

export interface HelcimInvoiceTaxContract {
  details?: string;
  amount?: number;
}

export interface HelcimInvoiceDiscountContract {
  details?: string;
  amount?: number;
}

export interface HelcimInvoiceResponse {
  organizationId: number;
  invoiceId: string;
  customerId?: string;
  invoiceNumber: string;
  currency: string;
  dateIssued?: string;
  type?: string;
  status?: string;
  convenienceFeeEnabled?: boolean;
  tipAmount?: number;
  notes?: string;
  lineItems: HelcimInvoiceLineItemContract[];
  billingAddress?: HelcimAddressResponse;
  shippingAddress?: HelcimAddressResponse;
  pickup?: HelcimInvoicePickupContract;
  tax?: HelcimInvoiceTaxContract;
  discount?: HelcimInvoiceDiscountContract;
}

export interface HelcimInvoiceListRequest {
  organizationId?: number;
  page?: number;
  dateStart?: string;
  dateEnd?: string;
  invoiceNumber?: string;
  customerId?: string;
  status?: string;
  type?: string;
  currency?: string;
  withConvenienceFee?: 0 | 1;
  withOrderFields?: 0 | 1;
  convenienceFeeEnabled?: 0 | 1;
}

export interface HelcimInvoiceListResponse {
  page?: number;
  limit?: number;
  totalCount?: number;
  invoices: HelcimInvoiceResponse[];
}

export interface HelcimInvoiceCreateRequest {
  organizationId: number;
  idempotencyKey?: string;
  customerId: string;
  invoiceNumber: string;
  currency: string;
  dateIssued?: string;
  lineItems: HelcimInvoiceLineItemContract[];
  billingAddress: HelcimAddressRequest;
  shippingAddress: HelcimAddressRequest;
  pickup?: HelcimInvoicePickupContract;
  tax?: HelcimInvoiceTaxContract;
  discount?: HelcimInvoiceDiscountContract;
  type?: string;
  status?: string;
  convenienceFeeEnabled?: boolean;
  tipAmount?: number;
  notes?: string;
}

export interface HelcimInvoiceUpdateRequest {
  organizationId: number;
  idempotencyKey?: string;
  invoiceId: string;
  customerId?: string;
  invoiceNumber?: string;
  currency?: string;
  dateIssued?: string;
  lineItems?: HelcimInvoiceLineItemContract[];
  billingAddress?: HelcimAddressRequest;
  shippingAddress?: HelcimAddressRequest;
  pickup?: HelcimInvoicePickupContract;
  tax?: HelcimInvoiceTaxContract;
  discount?: HelcimInvoiceDiscountContract;
  type?: string;
  status?: string;
  convenienceFeeEnabled?: boolean;
  tipAmount?: number;
  notes?: string;
}

export interface VerifyRequest {
  organizationId: number;
  ipAddress: string;
  currency: string;
  amount: number;
  cardToken: string;
  billingName: string;
  billingStreet1: string;
  billingPostalCode: string;
  customerCode?: string;
  invoiceNumber?: string;
  reference?: string;
}

export interface VerifyResponse {
  isVerified: boolean;
  status: string;
  cardToken?: string;
  approvalCode?: string;
  avsResponse?: string;
  cvvResponse?: string;
  errorCode?: string;
  errorMessage?: string;
  processedAtUtc: string;
}

export interface InitializeCheckoutRequest {
  organizationId?: number;
  amount: number;
  currency: string;
  paymentType?: 'purchase' | 'preauth' | 'verify';
  reference?: string;
  customerCode?: string;
  invoiceNumber?: string;
}

export interface InitializeCheckoutResponse {
  checkoutSessionId: string;
  checkoutToken: string;
  expiresAtUtc: string;
}

export interface CompleteCheckoutRequest {
  organizationId: number;
  checkoutSessionId: string;
  helcimPayloadJson: string;
  transactionId: string;
  amount: number;
  currency: string;
  cardToken?: string;
  hash: string;
  status: string;
  approvalCode?: string;
  dateCreated?: string;
  customerCode?: string;
  invoiceNumber?: string;
}

export interface CompleteCheckoutResponse {
  isValid: boolean;
  paymentRequestId: string;
  status: string;
  message?: string;
}

export interface PreauthRequest {
  organizationId: number;
  ipAddress: string;
  currency: string;
  amount: number;
  cardToken: string;
  billingName: string;
  billingStreet1: string;
  billingPostalCode: string;
  customerCode?: string;
  invoiceNumber?: string;
  reference?: string;
}

export interface PreauthResponse {
  isSuccess: boolean;
  status: string;
  preauthRecordId?: string;
  transactionId?: string;
  cardToken?: string;
  approvalCode?: string;
  errorCode?: string;
  errorMessage?: string;
  processedAtUtc: string;
}

@Injectable({
  providedIn: 'root'
})
export class TestPaymentService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = '/api/test-payments';
  private readonly checkoutApiUrl = '/api/payments/checkout';

  charge(request: TestPaymentRequest): Observable<TestPaymentResponse> {
    return this.http.post<TestPaymentResponse>(`${this.apiUrl}/charge`, request);
  }

  testConnection(): Observable<{ connected: boolean }> {
    return this.http.get<{ connected: boolean }>(`${this.apiUrl}/connection-test`);
  }

  initializeCheckout(request: InitializeCheckoutRequest): Observable<InitializeCheckoutResponse> {
    return this.http.post<InitializeCheckoutResponse>(`${this.checkoutApiUrl}/init`, request);
  }

  completeCheckout(request: CompleteCheckoutRequest): Observable<CompleteCheckoutResponse> {
    return this.http.post<CompleteCheckoutResponse>(`${this.checkoutApiUrl}/complete`, request);
  }

  preauth(request: PreauthRequest): Observable<PreauthResponse> {
    return this.http.post<PreauthResponse>(`${this.checkoutApiUrl}/preauth`, request);
  }

  verify(request: VerifyRequest): Observable<VerifyResponse> {
    return this.http.post<VerifyResponse>(`${this.checkoutApiUrl}/verify`, request);
  }

  getCustomers(request: HelcimCustomerListRequest): Observable<HelcimCustomerListResponse> {
    const query = new URLSearchParams();

    if (request.organizationId != null && request.organizationId > 0) {
      query.set('organizationId', String(request.organizationId));
    }

    if (request.search?.trim()) {
      query.set('search', request.search.trim());
    }

    if (request.customerCode?.trim()) {
      query.set('customerCode', request.customerCode.trim());
    }

    if (request.limit != null) {
      query.set('limit', String(request.limit));
    }

    if (request.page != null) {
      query.set('page', String(request.page));
    }

    if (request.includeCards != null) {
      query.set('includeCards', String(request.includeCards));
    }

    return this.http.get<HelcimCustomerListResponse>(`/api/payments/customers?${query.toString()}`);
  }

  getCustomer(organizationId: number, customerId: string): Observable<HelcimCustomerResponse> {
    return this.http.get<HelcimCustomerResponse>(`/api/payments/customers/${encodeURIComponent(customerId)}?organizationId=${organizationId}`);
  }

  updateCustomer(customerId: string, request: HelcimCustomerUpdateRequest): Observable<HelcimCustomerResponse> {
    return this.http.put<HelcimCustomerResponse>(`/api/payments/customers/${encodeURIComponent(customerId)}`, request);
  }

  createCustomer(request: HelcimCustomerCreateRequest): Observable<HelcimCustomerResponse> {
    return this.http.post<HelcimCustomerResponse>('/api/payments/customers', request);
  }

  getInvoices(request: HelcimInvoiceListRequest): Observable<HelcimInvoiceListResponse> {
    const query = new URLSearchParams();

    if (request.organizationId != null && request.organizationId > 0) {
      query.set('organizationId', String(request.organizationId));
    }

    if (request.page != null) {
      query.set('page', String(request.page));
    }

    if (request.dateStart?.trim()) {
      query.set('dateStart', request.dateStart.trim());
    }

    if (request.dateEnd?.trim()) {
      query.set('dateEnd', request.dateEnd.trim());
    }

    if (request.invoiceNumber?.trim()) {
      query.set('invoiceNumber', request.invoiceNumber.trim());
    }

    if (request.customerId?.trim()) {
      query.set('customerId', request.customerId.trim());
    }

    if (request.status?.trim()) {
      query.set('status', request.status.trim());
    }

    if (request.type?.trim()) {
      query.set('type', request.type.trim());
    }

    if (request.currency?.trim()) {
      query.set('currency', request.currency.trim().toUpperCase());
    }

    if (request.withConvenienceFee != null) {
      query.set('withConvenienceFee', String(request.withConvenienceFee));
    }

    if (request.withOrderFields != null) {
      query.set('withOrderFields', String(request.withOrderFields));
    }

    if (request.convenienceFeeEnabled != null) {
      query.set('convenienceFeeEnabled', String(request.convenienceFeeEnabled));
    }

    return this.http.get<HelcimInvoiceListResponse>(`/api/payments/invoices?${query.toString()}`);
  }

  getInvoice(organizationId: number, invoiceId: string): Observable<HelcimInvoiceResponse> {
    return this.http.get<HelcimInvoiceResponse>(`/api/payments/invoices/${encodeURIComponent(invoiceId)}?organizationId=${organizationId}`);
  }

  createInvoice(request: HelcimInvoiceCreateRequest): Observable<HelcimInvoiceResponse> {
    return this.http.post<HelcimInvoiceResponse>('/api/payments/invoices', request);
  }

  updateInvoice(invoiceId: string, request: HelcimInvoiceUpdateRequest): Observable<HelcimInvoiceResponse> {
    return this.http.put<HelcimInvoiceResponse>(`/api/payments/invoices/${encodeURIComponent(invoiceId)}`, request);
  }
}
